import { action }                from 'mobx';
import { computed }              from 'mobx';
import { makeObservable }        from 'mobx';
import { observable }            from 'mobx';
import { reaction }              from 'mobx';
import { runInAction }           from 'mobx';
import { Shape }                 from '@tokamap/core';
import ProjectModel              from '@models/map/ProjectModel';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import themeStore                from '@/TokamapEditor/stores/themeStore';
import { Theme }                 from '@/TokamapEditor/stores/themeStore';

const MAX_RECENT = 14;
const WRITE_DELAY_MS = 2000;

const normalize = (color: string) => color.trim().toLowerCase();

const isColor = (value: unknown): value is string =>
	typeof value === 'string' && !!value.trim() && normalize(value) !== 'transparent';

function unique(colors: unknown[]): string[] {
	return [...new Set(colors.filter(isColor).map(normalize))];
}

function readRecent(configuration: unknown): string[] {
	const recent = (configuration as { recentColors?: unknown } | undefined)?.recentColors;

	return Array.isArray(recent) ? unique(recent) : [];
}

function shapeColors(shape: Shape): unknown[] {
	return [
		shape.style?.bgColor, shape.style?.borderColor,
		shape.hoverStyle?.bgColor, shape.hoverStyle?.borderColor,
		shape.text?.color, shape.text?.bgColor,
		shape.hoverText?.color, shape.hoverText?.bgColor,
	];
}

function themeColors(theme: Theme): unknown[] {
	return [
		...Object.values(theme.appearance).flatMap(style => [style.bgColor, style.borderColor]),
		...Object.values(theme.text).flatMap(style => [style.color, style.bgColor]),
		theme.badge ? theme.badge.color : undefined,
	];
}

/**
 * Les couleurs employées dans la carte ouverte et les thèmes du projet. Les dernières choisies,
 * rangées dans la configuration du projet pour être partagées entre ses utilisateurs, passent
 * devant ; les autres suivent par fréquence.
 */
export class UsedColorsStore {
	@observable.ref private _chosen: string[] = [];

	private _timer?: ReturnType<typeof setTimeout>;

	constructor() {
		makeObservable(this);

		reaction(() => currentProjectEditorStore.get.project, (_project, previous) => {
			clearTimeout(this._timer);
			const chosen = this._chosen;
			runInAction(() => this._chosen = []);
			if (chosen.length) void this._write(previous, chosen);
		});
	}

	@computed get recent(): string[] {
		return unique([...this._chosen, ...readRecent(currentProjectEditorStore.get.project.configuration)]).slice(0, MAX_RECENT);
	}

	@computed get frequent(): string[] {
		const counts = new Map<string, number>();
		const colors = [
			...currentEditorStore.allShapes.flatMap(shapeColors),
			...themeStore.themes.flatMap(themeColors),
		];

		for (const color of colors) {
			if (!isColor(color)) continue;
			const key = normalize(color);
			counts.set(key, (counts.get(key) ?? 0) + 1);
		}

		return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([color]) => color);
	}

	/** Les couleurs présentes dans le projet, les récentes d'abord. */
	public colors(limit: number): string[] {
		const inUse = new Set(this.frequent);

		return unique([...this.recent.filter(color => inUse.has(color)), ...this.frequent]).slice(0, limit);
	}

	@action public remember(color: string) {
		if (!isColor(color)) return;

		this._chosen = unique([color, ...this._chosen]).slice(0, MAX_RECENT);

		clearTimeout(this._timer);
		const project = currentProjectEditorStore.get.project;
		this._timer = setTimeout(() => void this._write(project, this._chosen), WRITE_DELAY_MS);
	}

	private async _write(project: ProjectModel, chosen: string[]) {
		try {
			// Relu juste avant d'écrire : la configuration est envoyée en entier, et ce qu'un autre
			// utilisateur y a changé entre-temps ne doit pas être écrasé.
			await project.fetch();

			const stored = readRecent(project.configuration);
			const merged = unique([...chosen, ...stored]).slice(0, MAX_RECENT);

			if (merged.join() !== stored.join()) {
				await project.patch({ configuration: { ...project.configuration, recentColors: merged } });
			}

			runInAction(() => {
				this._chosen = this._chosen.filter(color => !chosen.includes(color));
			});
		} catch (err) {
			console.error('Couleurs récentes du projet', err);
		}
	}
}

export default new UsedColorsStore();
